import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
function Modal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return <AnimatePresence data-id="element-24">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100]" onClick={onClose} data-id="element-25" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl md:max-h-[80vh] bg-white rounded-2xl shadow-2xl z-[101] flex flex-col overflow-hidden" data-id="element-26">
            <div className="flex items-center justify-between p-6 border-b border-border" data-id="element-27">
              <h2 className="text-xl font-bold font-serif text-foreground" data-id="element-28">
                {title}
              </h2>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-accent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" aria-label="Close" data-id="element-29">
                <X className="w-5 h-5 text-foreground/60" data-id="element-30" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto text-foreground/80 leading-relaxed space-y-4" data-id="element-31">
              {children}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="py-10 px-4 border-t border-border bg-white" data-id="element-32">
        <div className="max-w-4xl mx-auto text-center" data-id="element-33">
          <div className="flex items-center justify-center gap-6 mb-4" data-id="element-34">
            <button onClick={() => setActiveModal('privacy')} className="text-sm text-foreground/60 hover:text-primary transition-colors underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded" data-id="element-35">
              Privacy Policy
            </button>
            <span className="text-foreground/30" data-id="element-36">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-sm text-foreground/60 hover:text-primary transition-colors underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded" data-id="element-37">
              Terms &amp; Conditions
            </button>
          </div>
          <p className="text-xs text-foreground/40" data-id="element-38">
            © {new Date().getFullYear()} Goodbye Diapers in 3 Days. All rights
            reserved.
          </p>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <Modal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Privacy Policy" data-id="element-39">
        <p data-id="element-40">
          <strong data-id="element-41">Last updated:</strong> February 2026
        </p>
        <p data-id="element-42">
          Your privacy is important to us. This Privacy Policy explains how we
          collect, use, and protect your personal information when you purchase
          and use "Goodbye Diapers in 3 Days."
        </p>
        <h3 className="font-bold text-foreground text-lg font-serif" data-id="element-43">
          Information We Collect
        </h3>
        <p data-id="element-44">
          When you make a purchase, we collect your name, email address, and
          payment information. Payment processing is handled securely by our
          third-party payment processor — we do not store your credit card
          details.
        </p>
        <h3 className="font-bold text-foreground text-lg font-serif" data-id="element-45">
          How We Use Your Information
        </h3>
        <ul className="list-disc pl-6 space-y-1" data-id="element-46">
          <li data-id="element-47">To deliver your digital product (PDF guide)</li>
          <li data-id="element-48">To send purchase confirmation and download links</li>
          <li data-id="element-49">To provide customer support if needed</li>
        </ul>
        <h3 className="font-bold text-foreground text-lg font-serif" data-id="element-50">
          Information Sharing
        </h3>
        <p data-id="element-51">
          We do not sell, trade, or share your personal information with third
          parties, except as necessary to process your payment and deliver your
          product.
        </p>
        <h3 className="font-bold text-foreground text-lg font-serif" data-id="element-52">
          Data Security
        </h3>
        <p data-id="element-53">
          We use industry-standard security measures to protect your personal
          information. All transactions are encrypted using SSL technology.
        </p>
        <h3 className="font-bold text-foreground text-lg font-serif" data-id="element-54">
          Your Rights
        </h3>
        <p data-id="element-55">
          You may request access to, correction of, or deletion of your personal
          data at any time by contacting us.
        </p>
      </Modal>

      {/* Terms & Conditions Modal */}
      <Modal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Terms & Conditions" data-id="element-56">
        <p data-id="element-57">
          <strong data-id="element-58">Last updated:</strong> February 2026
        </p>
        <p data-id="element-59">
          By purchasing "Goodbye Diapers in 3 Days," you agree to the following
          terms and conditions.
        </p>
        <h3 className="font-bold text-foreground text-lg font-serif" data-id="element-60">
          Digital Product
        </h3>
        <p data-id="element-61">
          This is a digital product delivered as a downloadable PDF. Upon
          purchase, you will receive immediate access to download the guide. Due
          to the digital nature of this product, all sales are final.
        </p>
        <h3 className="font-bold text-foreground text-lg font-serif" data-id="element-62">
          Permitted Use
        </h3>
        <ul className="list-disc pl-6 space-y-1" data-id="element-63">
          <li data-id="element-64">The guide is for personal, non-commercial use only</li>
          <li data-id="element-65">You may not redistribute, resell, or share the guide</li>
          <li data-id="element-66">
            You may not reproduce or modify the content for public distribution
          </li>
        </ul>
        <h3 className="font-bold text-foreground text-lg font-serif" data-id="element-67">
          Disclaimer
        </h3>
        <p data-id="element-68">
          The information provided in this guide is for educational purposes
          only and is not a substitute for professional medical advice. Every
          child is different, and results may vary. If your child has medical
          conditions affecting potty training, please consult your pediatrician.
        </p>
        <h3 className="font-bold text-foreground text-lg font-serif" data-id="element-69">
          Limitation of Liability
        </h3>
        <p data-id="element-70">
          We are not liable for any outcomes resulting from the use of this
          guide. The "guaranteed" language refers to our confidence in the
          method when followed as directed, not a financial guarantee.
        </p>
        <h3 className="font-bold text-foreground text-lg font-serif" data-id="element-71">
          Changes to Terms
        </h3>
        <p data-id="element-72">
          We reserve the right to update these terms at any time. Continued use
          of the product constitutes acceptance of any changes.
        </p>
      </Modal>
    </>;
}